import { QueryHookOptions, useApolloClient } from "@apollo/client";
import { ForwardedRef, forwardRef, useCallback } from "react";
import { GroupBase, SelectInstance } from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useAsyncFetchOptions from "components/Shared/Select/useAsyncFetchOptions";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertBrandId, assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useSelectStyles, { SelectThemingProps } from "utils/useSelectStyles";
import { gql, getFragmentData, DocumentType } from "__generated__/gql";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { BrandOrderingPlatformFilter } from "__generated__/gql/graphql";
import useLatestRef from "utils/useLatestRef";
import { isNewOption } from "components/Shared/Select/utils";
import { noOptionsMessage } from "components/Shared/Select/OrderingPlatformSelect";

const OPTION_LIMIT = 15;

const optionFragment = gql(/* GraphQL */ `
  fragment BrandOrderingPlatformSelectOption on BrandOrderingPlatform {
    id
    # Include orderingPlatformId for better caching
    orderingPlatformId
    orderingPlatform {
      id
      name
    }
  }
`);

export type Option = DocumentType<typeof optionFragment>;

const valueQuery = gql(/* GraphQL */ `
  query BrandOrderingPlatformSelectValue($ids: [UUID!]!) {
    brandOrderingPlatformsList(filter: { id: { in: $ids } }) {
      ...BrandOrderingPlatformSelectOption
    }
  }
`);

const query = gql(/* GraphQL */ `
  query BrandOrderingPlatformSelectOptions(
    $brandId: UUID!
    $filter: BrandOrderingPlatformFilter
    $limit: Int = 5
  ) {
    brandOrderingPlatforms(
      condition: { brandId: $brandId }
      filter: $filter
      orderBy: ORDERING_PLATFORM__NAME_ASC
      first: $limit
    ) {
      totalCount
      nodes {
        ...BrandOrderingPlatformSelectOption
      }
    }
  }
`);

function getOptionLabel(option: Option) {
  if (isNewOption(option)) return option.label;
  return option.orderingPlatform?.name;
}

export type BrandOrderingPlatformSelectProps<IsMulti extends boolean = false> =
  Omit<AsyncProps<Option, IsMulti, GroupBase<Option>>, "onChange" | "value"> &
    IdValueSelectProps<IsMulti> & {
      valueQueryOptions?: Omit<
        QueryHookOptions<
          DocumentType<typeof valueQuery>,
          DocumentVariablesType<typeof valueQuery>
        >,
        "variables"
      >;
    } & {
      excludedIds?: string[];
      filter?: BrandOrderingPlatformFilter;
      themingProps?: SelectThemingProps;
    };

export function useBrandOrderingPlatformSelect<
  IsMulti extends boolean = false,
>({
  components: componentsProp,
  excludedIds,
  isMulti,
  filter: filterProp,
  onChange,
  styles,
  themingProps,
  value: idValue,
  valueQueryOptions,
  ...rest
}: BrandOrderingPlatformSelectProps<IsMulti>) {
  const styleProps = useSelectStyles<Option, IsMulti>(styles, themingProps);
  const client = useApolloClient();
  const brandId = useCurrentBrandId();

  const idValueProps = useIdValue<Option, IsMulti, typeof valueQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.brandOrderingPlatformsList),
    getOptionLabel,
    isMulti,
    onChange,
    query: valueQuery,
    queryOptions: valueQueryOptions,
    value: idValue,
  });

  const latestFilterPropRef = useLatestRef(filterProp);
  const latestExcludedIdsRef = useLatestRef(excludedIds);

  const asyncFetchProps = useAsyncFetchOptions({
    cacheUsing: filterProp,
    fetchOptions: useCallback(
      (inputValue) => {
        const baseFilter: BrandOrderingPlatformFilter =
          /^[0-9a-f-]{32,36}$/i.test(inputValue)
            ? {
                or: [
                  { id: { equalTo: inputValue } },
                  { orderingPlatformId: { equalTo: inputValue } },
                ],
              }
            : {
                orderingPlatform: { name: { includesInsensitive: inputValue } },
              };

        const filters: BrandOrderingPlatformFilter[] = [baseFilter];

        if (latestExcludedIdsRef.current?.length) {
          filters.push({ id: { notIn: latestExcludedIdsRef.current } });
        }

        if (latestFilterPropRef.current) {
          filters.push(latestFilterPropRef.current);
        }

        const filter: BrandOrderingPlatformFilter =
          filters.length > 1 ? { and: filters } : filters[0];

        assertBrandId(brandId);

        return client
          .query({
            query,
            variables: { brandId, filter, limit: OPTION_LIMIT },
            fetchPolicy: "network-only",
            context: getActiveBrandIdContext(brandId),
          })
          .then(({ data }) => {
            assertPresence(
              data.brandOrderingPlatforms,
              "Could not get ordering platforms from response.",
            );
            const { nodes, totalCount } = data.brandOrderingPlatforms;
            const options = getFragmentData(optionFragment, nodes);
            return { options, totalCount };
          });
      },
      [brandId, client, latestFilterPropRef, latestExcludedIdsRef],
    ),
  });

  return {
    ...styleProps,
    ...idValueProps,
    ...asyncFetchProps,
    isMulti,
    noOptionsMessage,
    placeholder: "Select a customer…",
    components: {
      ...styleProps.components,
      ...asyncFetchProps.components,
      ...componentsProp,
    },
    ...rest,
  };
}

export type BrandOrderingPlatformSelectInstance<
  IsMulti extends boolean = false,
> = SelectInstance<Option, IsMulti>;

function BrandOrderingPlatformSelectInner<IsMulti extends boolean = false>(
  props: BrandOrderingPlatformSelectProps<IsMulti>,
  ref: ForwardedRef<BrandOrderingPlatformSelectInstance<IsMulti>>,
) {
  const selectProps = useBrandOrderingPlatformSelect<IsMulti>(props);

  return <AsyncSelect<Option, IsMulti> ref={ref} {...selectProps} />;
}

export default forwardRef(BrandOrderingPlatformSelectInner);
